import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink, Flame } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PageTransition from '../components/layout/PageTransition';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/layout/ScrollReveal';
import { getProjects } from '../lib/api';

const STATUS_FILTERS = ['All', 'Live', 'Beta', 'Coming Soon'];

export default function EcosystemPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    document.title = 'Ecosystem';
    getProjects().then((res) => setProjects(res.data.projects)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'All' ? projects : projects.filter((p) => p.status === filter);

  return (
    <PageTransition>
      <section className="relative pt-24 sm:pt-28 lg:pt-36 pb-14 sm:pb-20">
        <div className="absolute inset-0 bg-fire-fog" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal hero>
            <div className="flex items-center gap-2 mb-5"><Flame className="h-4 w-4 text-[#E8722A]" /><span className="kicker">Ecosystem</span></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[0.95] mb-6">Powered by Visualte</h1>
            <p className="text-base md:text-lg text-white/72 leading-relaxed max-w-xl">Products and platforms built on Visualte infrastructure. Each one running on the same reliable foundation.</p>
          </ScrollReveal>
        </div>
      </section>
      <section className="py-12 sm:py-14 relative" data-testid="ecosystem-grid-section">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="ecosystem-filter-row">
            {STATUS_FILTERS.map((s) => (
              <button key={s} data-testid={`filter-${s.toLowerCase().replace(' ', '-')}`} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-[4px] text-xs font-medium transition-all duration-200 border ${filter === s ? 'bg-[#E8722A]/10 text-[#E8722A] border-[#E8722A]/30' : 'bg-white/[0.03] text-white/55 border-white/10 hover:bg-white/[0.06] hover:text-white/80'}`}>{s}</button>
            ))}
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">{[1,2,3].map(i => <div key={i} className="hud-card border border-white/10 bg-white/[0.03] p-6"><Skeleton className="h-4 w-24 mb-3 bg-white/10" /><Skeleton className="h-6 w-40 mb-2 bg-white/10" /><Skeleton className="h-12 w-full bg-white/10" /></div>)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20"><p className="text-white/40 text-sm">No projects match this filter.</p></div>
          ) : (
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filtered.map((project) => (
                <StaggerItem key={project.id}>
                  <Link to={`/powered/${project.slug}`} data-testid="ecosystem-project-card">
                    <div className="hud-card border border-white/10 bg-white/[0.03] p-6 h-full card-hover-glow group">
                      <span className="kicker">{project.category || 'Platform'}</span>
                      <h3 className="text-xl md:text-2xl font-semibold tracking-[-0.02em] mt-2 mb-2 group-hover:text-[#E8722A] transition-colors duration-200">{project.name}</h3>
                      <StatusBadge status={project.status} />
                      <p className="text-sm text-white/55 leading-relaxed mt-3 mb-4 line-clamp-3">{project.tagline}</p>
                      {project.websiteUrl && <div className="flex items-center gap-1 text-xs text-white/40 mb-3"><ExternalLink className="h-3 w-3" /><span>{project.websiteUrl.replace(/^https?:\/\//, '')}</span></div>}
                      <div className="flex items-center gap-1 text-xs text-[#E8722A] group-hover:gap-2 transition-all duration-200"><span>View Details</span><ChevronRight className="h-3 w-3" /></div>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}
        </div>
      </section>
    </PageTransition>
  );
}

function StatusBadge({ status }) {
  const styles = { Live: 'bg-[rgba(232,114,42,0.12)] text-[rgba(240,170,100,0.9)] border-[rgba(232,114,42,0.2)]', Beta: 'bg-[rgba(240,170,60,0.10)] text-[rgba(240,190,100,0.88)] border-[rgba(240,170,60,0.18)]', 'Coming Soon': 'bg-white/8 text-white/70 border-white/12' };
  return <Badge className={`${styles[status] || styles['Coming Soon']} text-[10px] font-mono uppercase`}>{status === 'Live' && <span className="w-1.5 h-1.5 rounded-full bg-[#E8722A] mr-1.5 status-pulse inline-block" />}{status}</Badge>;
}
